// types
import {
  ClientResponse,
  DayInWeekRequest,
  EmployeeResponse,
  EventResponse,
  PlanningEntityIdentifier,
  ResourceResponse,
  WorkPlaceResponse
} from 'generated/index';
import { AppointmentFormFieldData } from '../../../AppointmentForm/AppointmentForm.types';
import { DayIndex } from '../../../AppointmentForm/components/RecurrencePatternBuilder/RecurrencePatternBuilder.types';
import type { CalendarAppointmentEvent } from '../../../Calendar/Calendar.types';
import type { ContextMenuAction } from '../../../ContextMenu/ContextMenu.types';
import type { CalendarSourceData } from 'components/mrp/stores/calendarStore/calendarStore.types';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

// utils
import dayjs from 'dayjs';
import { getLocale } from 'utils/date';
import i18n from 'config/i18n';
import { getIconColor, getIconType } from 'components/mrp/mrp.utils';
import {
  BuildEntityConfigsParams,
  EntityConfig
} from './CalendarAppointment.types';

const t = i18n.getFixedT(getLocale());

export const createAppointmentFormDataFromEventResponse = (
  planningEvent: Omit<EventResponse | Partial<EventResponse>, 'id'> & {
    id?: string;
  }
): AppointmentFormFieldData | null => {
  if (!planningEvent || Object.keys(planningEvent).length === 0) return null;

  const {
    id,
    activity,
    appointmentType,
    appointmentExceptions,
    clients = [],
    employees = [],
    workPlace,
    resources = [],
    start,
    end,
    notePublic,
    noteInternal,
    recurrenceRule,
    isRecurring,
    schedulingLabel,
    recurrenceRuleRepresentation,
    allowedToEditRecurrenceRule
  } = { id: '', ...planningEvent };

  return {
    id,
    start,
    originalOccurrenceStart: start,
    end,
    activity,
    appointmentType,
    appointmentExceptions,
    notePublic: notePublic || '',
    noteInternal: noteInternal || '',
    planEntities: {
      CLIENT: clients.map(
        ({ id: entityId, displayName: label = '', number }) => ({
          entityId,
          number,
          label,
          planningEntityType: 'CLIENT'
        })
      ),
      EMPLOYEE: employees
        .filter(Boolean)
        .map(({ entityId = '', nameInformal: label = '', number }) => ({
          entityId,
          number,
          label,
          planningEntityType: 'EMPLOYEE'
        })),
      RESOURCE: resources.map(({ id, name: label = '' }) => ({
        entityId: id,
        label,
        planningEntityType: 'RESOURCE'
      })),
      WORKPLACE: [
        {
          entityId: workPlace?.entityId || '',
          label: workPlace?.name || '',
          planningEntityType: 'WORKPLACE'
        }
      ]
    },
    recurrencePattern: {
      recurrenceRule: getRecurrenceRule(recurrenceRule),
      recurrenceInterval: recurrenceRule?.interval,
      recurrenceEnd:
        recurrenceRule?.recurrenceRange?.type === 'END_ON_DATE'
          ? recurrenceRule?.recurrenceRange?.end
          : undefined,
      daysSelected: recurrenceRule?.dayInWeekSet?.map((day) =>
        getDayIndexForWeekday(day)
      ),
      recurrenceRangeType:
        recurrenceRule?.recurrenceRange?.type || 'END_ON_DATE',
      recurrenceRuleRepresentation
    },
    schedulingLabel,
    isRecurring,
    allowedToEditRecurrenceRule
  };
};

const getRecurrenceRule = (
  rule: EventResponse['recurrenceRule']
): 'daily' | 'weekly' | 'none' => {
  if (!rule) return 'none';
  return rule.periodType === 'DAY' ? 'daily' : 'weekly';
};

const getDayIndexForWeekday = (weekday: DayInWeekRequest): DayIndex => {
  switch (weekday) {
    case 'MONDAY':
      return 0;
    case 'TUESDAY':
      return 1;
    case 'WEDNESDAY':
      return 2;
    case 'THURSDAY':
      return 3;
    case 'FRIDAY':
      return 4;
    case 'SATURDAY':
      return 5;
    case 'SUNDAY':
      return 6;
  }
};

export const extractEntityDataFromPlanningEvent = (
  event: CalendarAppointmentEvent,
  entityIds: string[] = []
) => {
  const { planningEvent } = event;

  const otherEmployees =
    planningEvent?.employees?.filter(
      ({ entityId }) =>
        entityIds.length != 1 || entityIds.indexOf(entityId!) == -1
    ) || [];
  const isEmployee = planningEvent?.employees?.some(
    ({ entityId }) =>
      entityIds.length == 1 && entityIds.indexOf(entityId!) != -1
  );

  const otherResources =
    planningEvent?.resources?.filter(
      ({ id }) => entityIds.length != 1 || entityIds.indexOf(id!) == -1
    ) || [];
  const isResource = planningEvent?.resources?.some(
    ({ id }) => entityIds.length == 1 && entityIds.indexOf(id!) != -1
  );

  const otherClients =
    planningEvent?.clients?.filter(
      ({ id }) => entityIds.length != 1 || entityIds.indexOf(id!) == -1
    ) || [];
  const isClient = planningEvent?.clients?.some(
    ({ id }) => entityIds.length == 1 && entityIds.indexOf(id!) != -1
  );

  const otherWorkPlace = ((entityIds.length != 1 ||
    entityIds.indexOf(planningEvent?.workPlace?.entityId!) == -1) &&
    planningEvent?.workPlace) as WorkPlaceResponse | undefined;

  return {
    otherEmployees,
    isEmployee,
    otherResources,
    isResource,
    otherClients,
    isClient,
    otherWorkPlace
  };
};

export const getPlanningEntityIdentifiersFromEvent = (event: EventResponse) => {
  if (!event) return [];

  return [
    ...event.clients?.map(
      ({ id }) =>
        ({
          entityId: id,
          planningEntityType: 'CLIENT'
        }) as PlanningEntityIdentifier
    ),
    ...event.employees?.map(
      ({ id, entityId, number }) =>
        ({
          entityId,
          id,
          number,
          planningEntityType: 'EMPLOYEE'
        }) as PlanningEntityIdentifier
    ),
    ...(event.resources?.map(
      ({ id }) =>
        ({
          entityId: id,
          planningEntityType: 'RESOURCE'
        }) as PlanningEntityIdentifier
    ) || []),
    ...(event.workPlace
      ? [
          {
            entityId: event.workPlace.entityId,
            planningEntityType: 'WORKPLACE'
          } as PlanningEntityIdentifier
        ]
      : [])
  ];
};

export const getOpenCalendarAction = (
  openCalendar: (sources: CalendarSourceData[], asPinned?: boolean) => void,
  openedCalendarEntityIds: string[],
  employees: EmployeeResponse[],
  clients: ClientResponse[],
  resources: ResourceResponse[],
  workPlace?: WorkPlaceResponse
) => {
  const openCalendarAction: ContextMenuAction = {
    value: t('mrp.addCalendar'),
    icon: {
      iconType: 'calendar-plus'
    },
    subActions: [
      ...employees.map(
        (x) =>
          ({
            value: x.nameInformal!,
            disabled: openedCalendarEntityIds.indexOf(x.entityId!) != -1,
            icon: {
              iconType: getIconType('EMPLOYEE'),
              iconColor: getIconColor('EMPLOYEE')
            },
            onClick: () =>
              openCalendar(
                [
                  {
                    label: x.nameInformal!,
                    planningEntityIdentifier: {
                      entityId: x.entityId,
                      number: x.number,
                      planningEntityType: 'EMPLOYEE'
                    }
                  }
                ],
                true
              )
          }) satisfies ContextMenuAction
      ),
      ...clients.map(
        (x) =>
          ({
            value: x.displayName!,
            disabled: openedCalendarEntityIds.indexOf(x.id) != -1,
            icon: {
              iconType: getIconType('CLIENT'),
              iconColor: getIconColor('CLIENT')
            },
            onClick: () =>
              openCalendar(
                [
                  {
                    label: x.displayName!,
                    planningEntityIdentifier: {
                      entityId: x.id,
                      number: x.number,
                      planningEntityType: 'CLIENT'
                    }
                  }
                ],
                true
              )
          }) satisfies ContextMenuAction
      ),
      ...resources.map(
        (x) =>
          ({
            value: x.name!,
            disabled: openedCalendarEntityIds.indexOf(x.id!) != -1,
            icon: {
              iconType: getIconType('RESOURCE'),
              iconColor: getIconColor('RESOURCE')
            },
            onClick: () =>
              openCalendar(
                [
                  {
                    label: x.name!,
                    planningEntityIdentifier: {
                      entityId: x.id,
                      planningEntityType: 'RESOURCE'
                    }
                  }
                ],
                true
              )
          }) satisfies ContextMenuAction
      ),
      ...(!!workPlace
        ? [
            {
              value: workPlace.name!,
              disabled:
                openedCalendarEntityIds.indexOf(workPlace.entityId!) != -1,
              icon: {
                iconType: getIconType('WORKPLACE'),
                iconColor: getIconColor('WORKPLACE')
              },
              onClick: () =>
                openCalendar(
                  [
                    {
                      label: workPlace.name!,
                      planningEntityIdentifier: {
                        entityId: workPlace.entityId,
                        planningEntityType: 'WORKPLACE'
                      }
                    }
                  ],
                  true
                )
            } satisfies ContextMenuAction
          ]
        : [])
    ]
  };

  return openCalendarAction.subActions!.length > 0 ? openCalendarAction : null;
};

export const buildEntityConfigs = ({
  palette,
  otherEmployees,
  otherClients,
  otherResources: resources,
  isEmployee,
  isClient,
  isResource
}: BuildEntityConfigsParams): EntityConfig[] => [
  {
    count: otherEmployees.length,
    singleLabel: otherEmployees[0]?.nameInformal,
    iconType: 'user-doctor' as IconType,
    pluralLabel: t('mrp.entityTypeMap.EMPLOYEE.plural'),
    isCurrentEntity: isEmployee,
    iconColor: palette.employee
  },
  {
    count: otherClients.length,
    singleLabel: otherClients[0]?.displayName,
    iconType: 'user' as IconType,
    pluralLabel: t('mrp.entityTypeMap.CLIENT.plural'),
    isCurrentEntity: isClient,
    iconColor: palette.client
  },
  {
    count: resources.length,
    singleLabel: resources[0]?.name,
    iconType: 'hammer-brush' as IconType,
    pluralLabel: t('mrp.entityTypeMap.RESOURCE.plural'),
    isCurrentEntity: isResource,
    iconColor: palette.resource
  }
];

export const sameDay = (date: Date) =>
  dayjs()
    .hour(dayjs(date).hour())
    .minute(dayjs(date).minute())
    .second(0)
    .millisecond(0);
